/**
 * Tab "Enviados" del Panel People — el registro pedido por People de qué
 * alertas mandó el flow: DMs de Slack, eventos de Calendar, avisos al
 * recruiter, errores, vencidos y bloqueos, de TODAS las altas, filtrable.
 * Fuente: OnboardingActionsTable vía peopleFlow.getRecentActions.
 *
 * Regla (feedback Sol 10/09): un "No se pudo" tiene que decir QUÉ DATO falta.
 * El detalle crudo del motor ('destinatario inválido para day1_dm ("vacío")')
 * pasa por actionDetailPlain y sale como "Falta 'Correo Winclap' en la
 * planilla" + qué hacer. Y cada línea nombra a la persona, no un ID.
 */

import { currentLocale } from '@/lib/locale';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  MessageCircle,
  CalendarPlus,
  AlertTriangle,
  Clock,
  ShieldAlert,
  RefreshCcw,
  User,
  Mail,
} from 'lucide-react';
import { trpc } from '@/lib/trpc';
import { WinclapLoader } from '@/components/ui/winclap-loader';
import { ACTION_PLAIN, actionDetailPlain, stepTitle } from '@/components/users/onboarding-flow/plain-language';

const STEP_OPTIONS = [
  'validation',
  'cal_day1',
  'cal_leader',
  'rep_reminder',
  'leader_notice',
  'day1_dm',
  'channels_dm',
  'learning_dm',
  'whatsapp_dm',
  'week1_form_dm',
  'global_dm',
  'global_followup_dm',
  'checkin30_dm',
  'alta_account',
];

function actionIcon(action: string) {
  switch (action) {
    case 'sent':
      return <MessageCircle className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />;
    case 'created':
      return <CalendarPlus className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-400" />;
    case 'validation_notified':
      return <Mail className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />;
    case 'error':
      return <AlertTriangle className="h-3.5 w-3.5 text-red-600 dark:text-red-400" />;
    case 'expired':
      return <Clock className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />;
    case 'waiting':
      return <Clock className="h-3.5 w-3.5 text-yellow-600 dark:text-yellow-400" />;
    case 'skipped_validation':
      return <ShieldAlert className="h-3.5 w-3.5 text-amber-600 dark:text-amber-400" />;
    case 'flags_reset':
      return <RefreshCcw className="h-3.5 w-3.5 text-purple-600 dark:text-purple-400" />;
    default:
      return <User className="h-3.5 w-3.5 text-muted-foreground" />;
  }
}

export function OnboardingSentTab() {
  const { t } = useTranslation();
  const [sinceDays, setSinceDays] = useState<7 | 14 | 30>(7);
  const [altaId, setAltaId] = useState('');
  const [stepId, setStepId] = useState<string>('all');

  const query = trpc.peopleFlow.getRecentActions.useQuery(
    {
      sinceDays,
      altaId: altaId.trim() || undefined,
      stepId: stepId === 'all' ? undefined : stepId,
    },
    { staleTime: 60_000 }
  );

  // El audit guarda solo el ID de alta. El nombre sale del estado del flow
  // (misma query cacheada que usa la tab Proceso) — "alta 285" no le dice
  // nada a nadie. Fuera de la ventana de 45 días queda el ID como fallback.
  const flowQuery = trpc.peopleFlow.getFlowStatus.useQuery(undefined, { staleTime: 60_000 });
  const nameByAlta = useMemo(
    () => new Map((flowQuery.data ?? []).map((r) => [r.alta.idAlta, r.alta.nombre])),
    [flowQuery.data]
  );

  const byDay = useMemo(() => {
    const groups = new Map<string, NonNullable<typeof query.data>>();
    for (const e of query.data ?? []) {
      const day = e.createdAt.slice(0, 10);
      if (!groups.has(day)) groups.set(day, []);
      groups.get(day)!.push(e);
    }
    return [...groups.entries()];
  }, [query.data]);

  return (
    <div className="space-y-4">
      <Card className="p-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">
              {t('users.people_panel.sent.period', 'Período')}
            </label>
            <div className="flex gap-1">
              {([7, 14, 30] as const).map((d) => (
                <button
                  key={d}
                  onClick={() => setSinceDays(d)}
                  className={`h-8 rounded-md border px-3 text-sm ${
                    sinceDays === d
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'bg-background hover:bg-muted'
                  }`}
                >
                  {d}d
                </button>
              ))}
            </div>
          </div>
          <div>
            {/* i18n-ok: nombre de la columna de la planilla */}
            <label className="mb-1 block text-xs text-muted-foreground">ID Alta</label>
            <Input
              className="h-8 w-28 text-sm"
              placeholder="285"
              value={altaId}
              onChange={(e) => setAltaId(e.target.value)}
            />
          </div>
          <div>
            <label className="mb-1 block text-xs text-muted-foreground">
              {t('users.people_panel.sent.step', 'Paso')}
            </label>
            <Select value={stepId} onValueChange={setStepId}>
              <SelectTrigger className="h-8 w-56 text-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('users.people_panel.sent.all_steps', 'Todos los pasos')}</SelectItem>
                {STEP_OPTIONS.map((s) => (
                  <SelectItem key={s} value={s}>
                    {stepTitle(t, s)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {(altaId || stepId !== 'all') && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8"
              onClick={() => {
                setAltaId('');
                setStepId('all');
              }}
            >
              {t('users.people_panel.sent.clear', 'Limpiar')}
            </Button>
          )}
        </div>
      </Card>

      {query.isLoading ? (
        <WinclapLoader />
      ) : (query.data ?? []).length === 0 ? (
        <Card className="p-10 text-center">
          <p className="text-sm font-medium">
            {t('users.people_panel.sent.empty', 'Sin acciones registradas para estos filtros')}
          </p>
        </Card>
      ) : (
        byDay.map(([day, entries]) => (
          <Card key={day} className="p-4">
            <h3 className="mb-2 text-sm font-semibold">
              {new Date(`${day}T12:00:00Z`).toLocaleDateString(currentLocale(), {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
              })}
            </h3>
            <div className="space-y-2">
              {entries.map((e) => {
                const plain = actionDetailPlain(t, e);
                const person = nameByAlta.get(e.altaId);
                const problem = e.action === 'error' || e.action === 'skipped_validation';
                return (
                  <div key={e.actionId} className="text-xs">
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                      <span className="shrink-0 tabular-nums text-muted-foreground">
                        {new Date(e.createdAt).toLocaleTimeString(currentLocale(), {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                      <span className="shrink-0">{actionIcon(e.action)}</span>
                      <span className="shrink-0 font-medium">
                        {ACTION_PLAIN(t)[e.action] ?? e.action}
                      </span>
                      {e.stepId && (
                        <span className="shrink-0 text-muted-foreground">
                          · {stepTitle(t, e.stepId)}
                        </span>
                      )}
                      <Link
                        to={`/admin/users/people-panel?tab=proceso&alta=${e.altaId}`}
                        className="shrink-0 text-primary hover:underline"
                      >
                        {person ?? t('users.people_panel.sent.alta_id', { id: e.altaId })}
                      </Link>
                      {!e.performedBy.startsWith('system:') && (
                        <span className="ml-auto shrink-0 text-muted-foreground">{e.performedBy}</span>
                      )}
                    </div>
                    {plain && (
                      <p
                        className={`ml-[3.6rem] ${
                          problem ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'
                        }`}
                      >
                        {plain.text}
                        {plain.todo && (
                          <span className="text-muted-foreground"> {plain.todo}</span>
                        )}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        ))
      )}
      <p className="text-[11px] text-muted-foreground">
        {t(
          'users.people_panel.sent.note',
          'Incluye DMs de Slack, eventos de Calendar, avisos al recruiter, errores, vencidos y bloqueos por datos de los últimos {{days}} días.',
          { days: sinceDays }
        )}
      </p>
    </div>
  );
}
